import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Label, LabelProps, Pie, PieChart } from "recharts";

import { GqlPlannerV2VisitSimplifiedStatus } from "~/__generated__/graphql/types";
import { theme } from "~/config/theme";

import type { ProjectStatistics_VisitStatusDistributionItemFragment } from "../fragments.generated";
import { ClickThroughLink } from "./ClickThroughLink";
import { ProjectStatisticUnavailableCard } from "./ProjectStatisticUnavailableCard";

interface ProjectVisitStatusDistributionCardProps {
    distribution: ProjectStatistics_VisitStatusDistributionItemFragment[];
}

export function ProjectVisitStatusDistributionCard({ distribution }: ProjectVisitStatusDistributionCardProps) {
    const { t } = useTranslation("project");
    const totalCount = distribution.reduce((sum, item) => sum + item.count, 0);

    if (totalCount < 1) {
        return <ProjectStatisticUnavailableCard title={t("overviewScreen.visitStatusDistribution.title")} />;
    }

    const pieChartElements = PROJECT_STATUS_TABLE_ORDER.map(status => {
        // biome-ignore lint/style/noNonNullAssertion: will always exist
        const element = distribution.find(x => x.status === status)!;

        return {
            name: element.status,
            value: element.count,
            fill: PROJECT_STATUS_COLORS[element.status],
        };
    });

    return (
        <Paper sx={{ display: "flex", flexDirection: "column", padding: 2, rowGap: 1 }} variant="outlined">
            <Typography variant="h6" gutterBottom>
                {t("overviewScreen.visitStatusDistribution.title")}
            </Typography>
            <PieChart responsive style={{ width: "100%", height: 200, pointerEvents: "none" }}>
                <Pie data={pieChartElements} dataKey="value" startAngle={180} endAngle={0} innerRadius="75%" outerRadius="100%" />
                <Label content={<PieChartLabel value1="Total visits" value2={totalCount} />} position="centerBottom" />
            </PieChart>
            <Table size="small" sx={{ marginTop: "-75px" }}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t("overviewScreen.visitStatusDistribution.status")}</TableCell>
                        <TableCell align="right">{t("overviewScreen.visitStatusDistribution.count")}</TableCell>
                        <TableCell align="right">{t("overviewScreen.visitStatusDistribution.percentage")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {pieChartElements.map(element => {
                        return (
                            <TableRow key={element.name}>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <StatusDot color={element.fill} />
                                        <span>{t(`overviewScreen.visitStatusDistribution.statusValue.${element.name}`)}</span>
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">{element.value}</TableCell>
                                <TableCell align="right">{((element.value / totalCount) * 100).toFixed(0)}%</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <ClickThroughLink href="planned" title={t("overviewScreen.visitStatusDistribution.link")} />
        </Paper>
    );
}

interface StatusDotProps {
    color: string;
}

function StatusDot({ color }: StatusDotProps) {
    return <Box borderRadius="25%" bgcolor={color} height={12} width={12} />;
}

interface PieChartLabelProps extends LabelProps {
    value1: ReactNode;
    value2: ReactNode;
}

function PieChartLabel({ viewBox, value1, value2 }: PieChartLabelProps) {
    const { typography, palette } = useTheme();
    // @ts-expect-error `viewBox` seems to be available
    const { cx, cy } = viewBox;

    return (
        <text x={cx} y={cy} textAnchor="middle">
            <tspan fontSize={typography.subtitle2.fontSize} x={cx} dy="-1.5em" fill={palette.text.secondary}>
                {value1}
            </tspan>
            <tspan fontSize={typography.body1.fontSize} x={cx} dy="1.2em" fill={palette.text.primary}>
                {value2}
            </tspan>
        </text>
    );
}

const PROJECT_STATUS_TABLE_ORDER = [
    GqlPlannerV2VisitSimplifiedStatus.Approved,
    GqlPlannerV2VisitSimplifiedStatus.EcoTeamReview,
    GqlPlannerV2VisitSimplifiedStatus.Planned,
    GqlPlannerV2VisitSimplifiedStatus.Unplanned,
];

const PROJECT_STATUS_COLORS: Record<GqlPlannerV2VisitSimplifiedStatus, string> = {
    [GqlPlannerV2VisitSimplifiedStatus.Approved]: theme.palette.success.main,
    [GqlPlannerV2VisitSimplifiedStatus.EcoTeamReview]: theme.palette.warning.main,
    [GqlPlannerV2VisitSimplifiedStatus.Planned]: theme.palette.info.main,
    [GqlPlannerV2VisitSimplifiedStatus.Unplanned]: theme.palette.grey[400],
};
